import { useCallback, useEffect, useState } from "react";

import { SessionWhiteboard } from "../domain/sessionWhiteboard";
import { SessionsAPI } from "../infrastructure/api/sessions.api";

export type SessionWhiteboardsState = {
    boards: SessionWhiteboard[];
    loading: boolean;
    loadFailed: boolean;
    deleteFailed: boolean;
    deletingId: string | null;
    remove: (whiteboardId: string) => Promise<void>;
    /** Re-fetch to mint fresh download URLs; the ones held expire. */
    refresh: () => void;
};

/**
 * The saved boards of one session, and what can be done to them.
 *
 * Transport lives here rather than in the panel that shows the list, so the
 * component stays a component and the fetching is testable without a DOM.
 */
export function useSessionWhiteboards(sessionId: string): SessionWhiteboardsState {
    const [boards, setBoards] = useState<SessionWhiteboard[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [deleteFailed, setDeleteFailed] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [reloads, setReloads] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setLoadFailed(false);

        SessionsAPI.getWhiteboards(sessionId)
            .then((items) => {
                if (!cancelled) setBoards(items);
            })
            .catch(() => {
                if (!cancelled) setLoadFailed(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [sessionId, reloads]);

    const refresh = useCallback(() => setReloads((count) => count + 1), []);

    const remove = useCallback(
        async (whiteboardId: string) => {
            setDeletingId(whiteboardId);
            setDeleteFailed(false);
            try {
                await SessionsAPI.deleteWhiteboard(sessionId, whiteboardId);
                setBoards((current) => current.filter((board) => board.id !== whiteboardId));
            } catch {
                // The board stays on screen. Pressing delete again finishes
                // the job — the image is already gone by then and removing a
                // missing one is not an error.
                setDeleteFailed(true);
            } finally {
                setDeletingId(null);
            }
        },
        [sessionId]
    );

    return { boards, loading, loadFailed, deleteFailed, deletingId, remove, refresh };
}

/**
 * Put a board on the session record.
 *
 * Not a hook: it is called from an event handler during a call, not from a
 * component's lifecycle, and the meeting page has no list to keep in step.
 */
export async function saveSessionWhiteboard(
    sessionId: string,
    imageBase64: string
): Promise<SessionWhiteboard> {
    return SessionsAPI.saveWhiteboard(sessionId, imageBase64);
}
